import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { AlertTriangle, AlertCircle, CheckCircle, X, Settings } from 'lucide-react';

export default function AlertNotificationCenter() {
  const [alerts, setAlerts] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [minThreshold, setMinThreshold] = useState(20);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 5 * 60 * 1000); // Co 5 minut
    return () => clearInterval(interval);
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await base44.entities.ProfitabilityAlert.filter(
        { status: 'active' },
        '-created_date',
        100
      );
      setAlerts(data || []);
      setActiveAlerts(data?.filter(a => a.status === 'active') || []);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (alertId, note) => {
    try {
      await base44.entities.ProfitabilityAlert.update(alertId, {
        status: 'resolved',
        resolvedAt: new Date().toISOString(),
        resolvedBy: (await base44.auth.me())?.email,
        resolutionNote: note,
      });
      await loadAlerts();
    } catch (error) {
      console.error('Error resolving alert:', error);
      alert('Błąd przy rozwiązywaniu alertu');
    }
  };

  const handleIgnore = async (alertId) => {
    try {
      await base44.entities.ProfitabilityAlert.update(alertId, {
        status: 'ignored',
      });
      await loadAlerts();
    } catch (error) {
      console.error('Error ignoring alert:', error);
    }
  };

  if (loading && activeAlerts.length === 0) {
    return null; // Nie wyświetlaj nic jeśli ładuje i brak alertów
  }

  if (activeAlerts.length === 0 && !showSettings) {
    return null; // Brak aktywnych alertów
  }

  const criticalCount = activeAlerts.filter(a => a.severity === 'critical').length;
  const warningCount = activeAlerts.filter(a => a.severity === 'warning').length;

  return (
    <div className="fixed bottom-6 right-6 z-40 w-full max-w-md">
      {/* Mini notification badge */}
      {activeAlerts.length > 0 && !showSettings && (
        <button
          onClick={() => setShowSettings(true)}
          className="w-full mb-3 p-3 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-center gap-3 hover:bg-red-100 transition-colors"
        >
          <div className="flex-1">
            <p className="font-semibold text-red-900 text-sm">
              {criticalCount > 0 ? `${criticalCount} krytycznych ` : ''}{warningCount > 0 ? `${warningCount} ostrzeżeń` : ''} marż
            </p>
            <p className="text-xs text-red-700">Kliknij aby zobaczyć szczegóły</p>
          </div>
          <AlertTriangle size={20} className="text-red-500 shrink-0" />
        </button>
      )}

      {/* Full notification panel */}
      {showSettings && (
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-white text-sm">Alerty Rentowności</h3>
              <p className="text-xs text-red-100">{activeAlerts.length} aktywnych alertów</p>
            </div>
            <button
              onClick={() => setShowSettings(false)}
              className="p-1 hover:bg-red-600 rounded transition-colors text-white"
            >
              <X size={18} />
            </button>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {activeAlerts.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                <CheckCircle size={32} className="mx-auto mb-2 text-green-500" />
                Brak aktywnych alertów
              </div>
            ) : (
              <div className="divide-y">
                {activeAlerts.map(alert => (
                  <AlertItem
                    key={alert.id}
                    alert={alert}
                    onResolve={handleResolve}
                    onIgnore={handleIgnore}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Settings Footer */}
          <div className="bg-gray-50 border-t px-4 py-3 text-xs">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showSettings}
                onChange={() => setShowSettings(!showSettings)}
                className="w-3 h-3"
              />
              <span className="text-gray-600">Utrzymaj otwarty</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function AlertItem({ alert, onResolve, onIgnore }) {
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const isCritical = alert.severity === 'critical';
  const marginGap = alert.minMarginThreshold - alert.currentMargin;

  return (
    <div
      className={`p-4 ${
        isCritical ? 'bg-red-50 border-l-4 border-red-500' : 'bg-yellow-50 border-l-4 border-yellow-500'
      }`}
    >
      <div className="flex gap-3 mb-2">
        {isCritical ? (
          <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5" />
        ) : (
          <AlertCircle size={18} className="text-yellow-500 shrink-0 mt-0.5" />
        )}
        <div className="flex-1">
          <p className="font-semibold text-sm text-gray-900">{alert.productName}</p>
          <p className="text-xs text-gray-600">Model: {alert.productModel}</p>
          <div className="mt-1 space-y-1 text-xs">
            <p className={isCritical ? 'text-red-700' : 'text-yellow-700'}>
              <strong>Obecna marża:</strong> {alert.currentMargin.toFixed(1)}% (próg: {alert.minMarginThreshold}%)
            </p>
            <p className={isCritical ? 'text-red-700' : 'text-yellow-700'}>
              <strong>Deficyt:</strong> {marginGap.toFixed(1)} pp
            </p>
            {alert.reason && (
              <p className="text-gray-600">
                <strong>Powód:</strong> {alert.reason}
              </p>
            )}
            {alert.suggestedAction && (
              <p className="text-gray-600">
                <strong>Sugestia:</strong> {alert.suggestedAction}
              </p>
            )}
          </div>

          {/* Resolve form */}
          {showResolveForm && (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
              <textarea
                value={resolveNote}
                onChange={e => setResolveNote(e.target.value)}
                placeholder="Jak rozwiązałeś ten alert? (np. podniosłem cenę o 5%)"
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500 resize-none"
                rows="2"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onResolve(alert.id, resolveNote);
                    setShowResolveForm(false);
                  }}
                  className="flex-1 px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors font-semibold"
                >
                  Potwierdź rozwiązanie
                </button>
                <button
                  onClick={() => setShowResolveForm(false)}
                  className="flex-1 px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400 transition-colors"
                >
                  Anuluj
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3">
        {!showResolveForm && (
          <>
            <button
              onClick={() => setShowResolveForm(true)}
              className="flex-1 px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors font-semibold"
            >
              Rozwiąż
            </button>
            <button
              onClick={() => onIgnore(alert.id)}
              className="flex-1 px-3 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500 transition-colors"
            >
              Ignoruj
            </button>
          </>
        )}
      </div>
    </div>
  );
}